import {
  Control,
  Controller,
  FieldValues,
  Path,
  PathValue,
} from "react-hook-form";

import ClockIcon from "public/icons/clock.svg";

import { CSSObject } from "@mui/material";
import {
  LocalizationProvider,
  TimePicker as MUITimePicker,
  TimePickerProps,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

import { PickFieldsByValueType } from "@/models/utils.models";
import { scrollBarSxStyles } from "@/shared/styles";
import { theme } from "@/theme/theme";

import { SvgIcon } from "../ui/svg-icon/SvgIcon";

type PropTypes<TFieldValues extends FieldValues> = {
  name: Path<PickFieldsByValueType<TFieldValues, Date | null>>;
  control: Control<TFieldValues>;
  label?: string;
  defaultValue?: Date | null;
} & TimePickerProps<Date>;

export const TimePicker = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  defaultValue = null,
  ...restProps
}: PropTypes<TFieldValues>) => {
  const { slotProps, ...rest } = restProps;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Controller
        name={name as Path<TFieldValues>}
        control={control}
        defaultValue={
          defaultValue as PathValue<TFieldValues, Path<TFieldValues>>
        }
        render={({ field }) => {
          return (
            <MUITimePicker
              value={field.value}
              onChange={(value) => field.onChange(value)}
              sx={muiTimePickerSx}
              ampm={false}
              label={label}
              slotProps={{
                popper: { sx: popperSx, ...slotProps?.popper },
                ...slotProps,
              }}
              slots={{
                openPickerIcon: () => (
                  <SvgIcon color="light-grey">
                    <ClockIcon />
                  </SvgIcon>
                ),
              }}
              {...rest}
            />
          );
        }}
      />
    </LocalizationProvider>
  );
};

const muiTimePickerSx: CSSObject = {
  "& .MuiOutlinedInput-root": {
    height: "40px",
  },

  "& input": {
    height: "40px",
    boxSizing: "border-box",
  },

  "& label:not(.Mui-focused):not(.MuiInputLabel-shrink)": {
    top: "-5px",
  },
};

const popperSx: CSSObject = {
  "& .MuiMultiSectionDigitalClockSection-root": scrollBarSxStyles,

  "& .MuiMenuItem-root": {
    borderRadius: "4px",
  },

  "& .Mui-selected": {
    backgroundImage: `linear-gradient(to right, ${theme.palette.blue.main}, ${theme.palette.green.main})`, // #52D1DA, #33D292
    color: theme.palette.white.main + "!important",
  },

  "& .MuiDialogActions-root": {
    display: "flex",
    justifyContent: "center",
    button: {
      backgroundImage: `linear-gradient(to right, ${theme.palette.blue.main}, ${theme.palette.green.main})`, // #52D1DA, #33D292
      color: theme.palette.white.main,
      borderRadius: "20px",
      paddingInline: "35px",
    },
  },

  "& .MuiMultiSectionDigitalClockSection-root::after": {
    height: 0,
  },
};
